import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { Link } from "react-router-dom";
import { LuPackageMinus, LuPencil, LuPlus } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    FilterButton,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { formatThousands, formatDate } from "@/utils";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

const initialFilters = {
    warehouse_id: "",
    search: "",
    date_from: "",
    date_to: "",
};

const StockOutData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("stock-out.edit");
    const table = useDataTable(
        "stock-history/data?activity_source=stock_out",
        null,
        {},
        initialFilters,
    );
    const { filterPanel } = table;

    const { data: warehouses } = useFetchData("select/warehouse");
    const filterOptions = { warehouses: warehouses || [] };

    const filterLabels = {
        warehouse_id: {
            label: "Gudang",
            getValueLabel: (val) => {
                const item = (warehouses || []).find(
                    (w) => String(w.value) === String(val),
                );
                return item?.title || val;
            },
        },
        search: { label: "Pencarian" },
        date_from: { label: "Dari Tanggal" },
        date_to: { label: "Sampai Tanggal" },
    };

    const handleRemoveFilter = (key) => {
        if (key === "date_from" || key === "date_to") {
            filterPanel.setPending((prev) => ({
                ...prev,
                date_from: "",
                date_to: "",
            }));
            table.filters.setFilter("date_from", "");
            table.filters.setFilter("date_to", "");
        } else {
            filterPanel.setPending((prev) => ({
                ...prev,
                [key]: initialFilters[key],
            }));
            table.filters.setFilter(key, initialFilters[key]);
        }
    };

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Produk", skeletonWidth: "w-40" },
        { name: "Gudang", skeletonWidth: "w-28" },
        { name: "Qty", skeletonWidth: "w-20", align: "end" },
        { name: "Satuan", skeletonWidth: "w-20" },
        { name: "Keterangan", skeletonWidth: "w-32" },
        ...(hasAnyAction
            ? [{ name: "Aksi", skeletonWidth: "w-16", align: "end" }]
            : []),
    ];

    const columns = [
        { key: "no", label: "No.", width: 50 },
        { key: "date", label: "Tanggal" },
        { key: "material", label: "Produk" },
        { key: "warehouse", label: "Gudang" },
        { key: "qty", label: "Qty", align: "end" },
        { key: "unit", label: "Satuan" },
        { key: "desc", label: "Keterangan" },
        ...(hasAnyAction
            ? [{ key: "actions", label: "Aksi", align: "end" }]
            : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </ThemedText>
                );
            case "date":
                return (
                    <ThemedText size="sm">{formatDate(item.date)}</ThemedText>
                );
            case "material":
                return (
                    <ThemedText size="sm">
                        {item.material?.name || "-"}
                    </ThemedText>
                );
            case "warehouse":
                return (
                    <ThemedText size="sm">
                        {item.warehouse?.name || "-"}
                    </ThemedText>
                );
            case "qty":
                return (
                    <ThemedText size="sm">
                        {formatThousands(item.qty_out)}
                    </ThemedText>
                );
            case "unit":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {item.unit?.name || "-"}
                    </ThemedText>
                );
            case "desc":
                return (
                    <ThemedText
                        size="xs"
                        variant="caption"
                        className="line-clamp-2"
                    >
                        {item.description || item.storage_location || "-"}
                    </ThemedText>
                );
            case "actions":
                return (
                    <div className="relative flex items-center justify-end gap-2">
                        <Tooltip content="Edit">
                            <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                as={Link}
                                to={`/stock-out/edit/${item.id}`}
                            >
                                <LuPencil
                                    size={14}
                                    className="text-default-500"
                                />
                            </Button>
                        </Tooltip>
                    </div>
                );
            default:
                return null;
        }
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Stok Keluar"
                    description="Terjadi kesalahan saat memuat data."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Stok Keluar"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Stok Keluar" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={filterPanel.isOpen}
                        onToggle={() =>
                            filterPanel.setIsOpen(!filterPanel.isOpen)
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("stock-out.create") && (
                        <Button
                            as={Link}
                            to="/stock-out/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Stok Keluar
                        </Button>
                    )}
                </div>
            </div>

            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            icon={LuPackageMinus}
                            title="Belum Ada Stok Keluar"
                            description="Klik tombol 'Tambah Stok Keluar' untuk menambahkan data stok keluar."
                        />
                    ) : (
                        <Table aria-label="Tabel Stok Keluar" shadow="none">
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                        width={column.width}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default StockOutData;
